import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ElementRef, ChangeDetectionStrategy} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TrimTextTransformPipe } from 'src/app/shared/pipes/trim-text-transform.pipe';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Subject, forkJoin, Observable, take, takeUntil } from 'rxjs';
import { AppConstants } from 'src/app/shared/app.constants';
import { LocationService } from 'src/app/services/location.service';
import { LookupService } from 'src/app/services/lookup.service';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { AuthService } from '../../../auth/auth.service';

@Component({
  selector: 'ness-find-existing-location',
  templateUrl: './find-existing-location.component.html',
  providers: [TrimTextTransformPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FindExistingLocationComponent implements OnInit, OnDestroy {
  @Input() associationButtonLabel: string = null;
  @Output() associateLocation = new EventEmitter<ServerModels.ENESS_AddressDTO[]>();
  @Output() closeForm = new EventEmitter();

  hasEditPermission = this.authService.hasUserTypeRole(AppPermissions.canEditLocation.roleUserType);
  findLocationFormGroup: FormGroup;
  rows: number = AppConstants.defaultTableRows;
  protected destroyed$: Subject<void> = new Subject();
  searchAddress$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  selectedAddresses: ServerModels.ENESS_AddressDTO[] = [];
  locationSearchResultTableCols = [
    { field: 'StreetAddress', header: 'Address' },
    { field: 'CityName', header: 'City' },
    { field: 'StateName', header: 'State' },
    { field: 'PostalCode', header: 'Postal code' }
  ];
  addressResultsSubj: BehaviorSubject<ServerModels.ENESS_AddressDTO[]> = new BehaviorSubject([]);
  addressResults$: Observable<ServerModels.ENESS_AddressDTO[]> = this.addressResultsSubj.asObservable();

  private _locationAddress: ServerModels.ENESS_AddressDTO = null;
  private locationAddress: BehaviorSubject<ServerModels.ENESS_AddressDTO> = new BehaviorSubject<ServerModels.ENESS_AddressDTO>(this._locationAddress);
  public locationAddress$: Observable<ServerModels.ENESS_AddressDTO> = this.locationAddress.asObservable();


  openLocationViewDetailForm: boolean = false;
  openLocationViewDetail: boolean = false;
  showLocationDetailsView: boolean = false;
  showEditRMSLocationView: boolean = false;

  constructor(
    public locationService: LocationService,
    private authService: AuthService,
    private fb: FormBuilder,
    private trimTextTransform: TrimTextTransformPipe,
    public messageService: MessageService,
    public lookupService: LookupService,
    public elementRef: ElementRef
  ) { }

  ngOnInit() {
    this.findLocationFormGroup = this.fb.group({
      FindLocationStreetAddressID: [null, [Validators.required, Validators.maxLength(80)]],
      FindLocationUnitID: [null, [Validators.maxLength(80)]],
      FindLocationCityNameID: null,
      FindLocationStateID: '',
      FindLocationPostalCodeID: [null, [Validators.pattern('[0-9]{5}')]],
      FindLocationPostalCodeExtendedID: [null, [Validators.pattern('[0-9]{4}')]]
    });
    this.onSearchLocationChanges();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onSearchLocation() {
    if (this.findLocationFormGroup.valid) {
      let locationSearch = this.findLocationFormGroup.value;
      let streetAddressValue = this.trimTextTransform.transform(locationSearch.FindLocationStreetAddressID);
      let unitValue = this.trimTextTransform.transform(locationSearch.FindLocationUnitID);
      let cityNameValue = this.trimTextTransform.transform(locationSearch.FindLocationCityNameID);
      let stateValue = this.trimTextTransform.transform(locationSearch.FindLocationStateID);
      let postalCodeValue = this.trimTextTransform.transform(locationSearch.FindLocationPostalCodeID);
      let postalCodeExtendedValue = this.trimTextTransform.transform(locationSearch.FindLocationPostalCodeExtendedID);

      this.findAddresses(streetAddressValue, unitValue, cityNameValue, stateValue, postalCodeValue, postalCodeExtendedValue);
    }
  }

  public onSearchLocationChanges(): void {
    this.findLocationFormGroup.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((val:any) => {
      if (!!val) {
        let streetAddressValue = !!val['FindLocationStreetAddressID'] ? val['FindLocationStreetAddressID'].trim() : "";
        let unitValue = !!val['FindLocationUnitID'] ? ", " + val['FindLocationUnitID'].trim() : "";
        let cityNameValue = !!val['FindLocationCityNameID'] ? ", " + val['FindLocationCityNameID'].trim() : "";
        let stateValue = !!val['FindLocationStateID'] ? ", " + val['FindLocationStateID'].trim() : "";
        let postalCodeValue = !!val['FindLocationPostalCodeID'] ? ", " + val['FindLocationPostalCodeID'].trim() : "";
        let postalCodeExtendedValue = !!val['FindLocationPostalCodeExtendedID'] ? "-" + val['FindLocationPostalCodeExtendedID'].trim() : "";

        this.searchAddress$.next(streetAddressValue + unitValue + cityNameValue + stateValue + postalCodeValue + postalCodeExtendedValue);
      }
    });
  }

  public associateSelectedLocations(): void { 
    this.associateLocation.emit(this.selectedAddresses);
    this.clearData();
  }

  public viewLocationDetails(id: string, source: string): void {
    this.openLocationViewDetailForm = true;
    this.openLocationViewDetail = true;
    this.showLocationDetailsView = true;
 
    forkJoin({
      address:this.locationService.getAddressDetailsById(id),
      leCases:this.locationService.getAddressLECases(id),
      people:this.locationService.getAddressPeople(id)
    }).pipe(take(1)).subscribe({
      next: (val: { address: ServerModels.ENESS_AddressDTO, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
        this._locationAddress = val.address;
        this.locationAddress.next(val.address);
        this.locationService.addressLECasesSubj.next(val.leCases);
        this.locationService.addressPeopleSubj.next(val.people);
      }
    });
  }

  public showEditLocation(evt: string): void {
    this.showLocationDetailsView = false;
    this.showEditRMSLocationView = true;
  }

  public onCloseLocation(): void {
    if (this.showEditRMSLocationView) {
      this.showEditRMSLocationView = false;
      this.showLocationDetailsView = true;
    }
    else if (this.showLocationDetailsView) {
      this._locationAddress = null;
      this.locationService.addressLECasesSubj.next(null);
      this.locationService.addressPeopleSubj.next(null);
      this.openLocationViewDetail = false;
      this.showLocationDetailsView = false;
      this.showEditRMSLocationView = false;
      this.openLocationViewDetailForm = false;
    }
  }

  public findAddresses(streetAddress: string, unit: string, cityName: string, state: string, postalCode: string, postalCodeExtended: string): void {
    this.addressResultsSubj.next([]);
    this.locationService.searchAddresses(streetAddress, unit, cityName, state, postalCode, postalCodeExtended).pipe(take(1)).subscribe((address: ServerModels.ENESS_AddressDTO[]) => {
      this.addressResultsSubj.next([...address]);
    });
  }

  public onMaskBlur(field: string) {
    let ctrl = this.findLocationFormGroup.get(field);
    if (ctrl.invalid) {
      ctrl.reset();
    }
  }

  public clearData(): void {
    this.findLocationFormGroup.reset({
      FindLocationStreetAddressID: '',
      FindLocationUnitID: '',
      FindLocationCityNameID: '',
      FindLocationStateID: '',
      FindLocationPostalCodeID: '',
      FindLocationPostalCodeExtendedID: ''
    });

    this.messageService.clear();
    this.selectedAddresses = [];
    this.addressResultsSubj.next([]);
  }

  public onShowSidebar(): void {
    let targetEl = this.elementRef.nativeElement.getElementsByClassName('ui-sidebar-active');
    if (targetEl.length) {
      targetEl[0].scrollTop = 0;
      targetEl[0].setAttribute("tabindex", "-1");
      targetEl[0].focus();
    }
  }

  public onCloseSidebar(): void {
    this.onCloseLocation();
    this._locationAddress = null;
    this.locationService.addressLECasesSubj.next(null);
    this.locationService.addressPeopleSubj.next(null);
    this.openLocationViewDetailForm = false;
  }

  public onLocationFormSaved(location: ServerModels.ENESS_AddressDTO): void {
    this.showEditRMSLocationView = false;
    this.showLocationDetailsView = true;
  }
}
